import type { IconName } from '../components/Icon.tsx';
import { locale, t, type MessageKey } from '../i18n/index.ts';
import { day, money, qty } from './format.ts';

/** Reports of your own (Customize): sources, columns and a report's definition, as the server keeps them. */

export type ColType = 'text' | 'number' | 'money' | 'date' | 'bool';
export type Agg = 'sum' | 'count' | 'avg' | 'min' | 'max';
export type Op = 'is' | 'is_not' | 'contains' | 'gt' | 'lt' | 'empty' | 'not_empty';
export type Period = 'all' | 'today' | 'this_week' | 'this_month' | 'last_month' | 'this_year' | 'last_year' | 'last_30' | 'last_365' | 'custom';
export type Bucket = 'day' | 'month' | 'year';
export type Chart = 'table' | 'bar' | 'line' | 'pivot';

export interface SourceCol {
  key: string;
  label: string;
  type: ColType;
  values?: string[];
  custom?: boolean;
  group?: string;
}
export interface Source {
  key: string;
  label: string;
  date: string;
  columns: SourceCol[];
}
export interface Filter {
  col: string;
  op: Op;
  value?: string | number | boolean | null;
}
export interface Spec {
  source: string;
  columns: string[];
  group: string[];
  measures: { col: string; agg: Agg }[];
  filters: Filter[];
  period: Period;
  from?: string | null;
  to?: string | null;
  sort?: { key: string; desc: boolean } | null;
  chart: Chart;
}
export interface OutCol {
  key: string;
  label: string;
  type: ColType;
  agg?: Agg;
  bucket?: Bucket;
  custom?: boolean;
  group?: string;
}
export interface Result {
  columns: OutCol[];
  rows: unknown[][];
  truncated: boolean;
  from: string | null;
  to: string | null;
  /** The database's totals of a summary: overall, per row group and per column group. */
  totals?: { grand: unknown[]; rows: unknown[][]; cols: unknown[][] } | null;
  /** A saved report's figures are worked out ahead and kept a few minutes: when, and whether reused. */
  computed_at?: string;
  cached?: boolean;
}
export interface SavedReport {
  id: string;
  name: string;
  source: string;
  spec: Spec;
  shared: boolean;
  mine: boolean;
  by: string | null;
  can_edit: boolean;
}

export const PERIODS: Period[] = ['today', 'this_week', 'this_month', 'last_month', 'last_30', 'this_year', 'last_year', 'last_365', 'all', 'custom'];
export const AGGS: Agg[] = ['sum', 'avg', 'min', 'max'];
export const SOURCE_ICON: Record<string, IconName> = {
  invoices: 'invoice',
  invoice_lines: 'list',
  sales: 'receipt',
  sales_lines: 'chef',
  bills: 'bag',
  customers: 'users',
  products: 'store',
  stock: 'box',
  payroll: 'cash',
  leads: 'trendUp',
};

/** A translation when there is one, else the server's English. */
function tr(key: string, fallback: string): string {
  const v = t(key as MessageKey);
  return v === key ? fallback : v;
}
const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

export const sourceLabel = (s: { key: string; label: string }) => tr(`rpt.src.${s.key}`, s.label);
/** A column's name; fields a business added keep their own name, with their record's in front when it is another one. */
export function colLabel(c: { label: string; custom?: boolean; group?: string }): string {
  const own = c.custom ? c.label : tr(`rpt.col.${slug(c.label)}`, c.label);
  return c.group ? `${tr(`rpt.col.${slug(c.group)}`, c.group)}: ${own}` : own;
}
export const valueLabel = (c: { custom?: boolean }, v: string) => (c.custom ? v : tr(`rpt.val.${v}`, v));

/** The heading of a result column: "Total", "Total (average)", "Month". */
export function outLabel(c: OutCol): string {
  if (c.agg === 'count') return t('rpt.count');
  const base = colLabel(c);
  if (c.agg) return t(`rpt.agg_${c.agg}` as MessageKey, { col: base });
  // A date grouped by month reads "Month", unless the column has a name of its own.
  if (c.bucket) return c.custom ? `${base} · ${t(`rpt.bucket_${c.bucket}` as MessageKey)}` : t(`rpt.unit_${c.bucket}` as MessageKey);
  return base;
}

/** One cell as people read it. */
export function cellText(c: OutCol & { values?: string[] }, v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  switch (c.type) {
    case 'money':
      return money(Number(v));
    case 'number':
      return c.agg === 'avg' ? new Intl.NumberFormat(locale(), { maximumFractionDigits: 2 }).format(Number(v)) : qty(Number(v));
    case 'date':
      if (c.bucket === 'month' && !c.agg) return new Intl.DateTimeFormat(locale(), { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${String(v)}T00:00:00Z`));
      if (c.bucket === 'year' && !c.agg) return String(v).slice(0, 4);
      return day(String(v));
    case 'bool':
      return v ? t('rpt.yes') : t('rpt.no');
    default:
      return c.agg ? String(v) : valueLabel(c, String(v));
  }
}

/** A plain value for a spreadsheet: amounts in lek with decimals, dates as YYYY-MM-DD. */
export function csvValue(c: OutCol, v: unknown): string | number {
  if (v === null || v === undefined) return '';
  if (c.type === 'money') return (Number(v) / 100).toFixed(2);
  if (c.type === 'bool') return v ? t('rpt.yes') : t('rpt.no');
  if (c.type === 'text' && !c.agg) return valueLabel(c, String(v));
  return v as string | number;
}

/** Whether a column adds up in the totals line. */
export const adds = (c: OutCol) => (c.agg ? c.agg === 'sum' || c.agg === 'count' : c.type === 'money');

export const emptySpec = (source: string): Spec => ({ source, columns: [], group: [], measures: [], filters: [], period: 'this_month', sort: null, chart: 'table' });
